import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import moment from 'moment';
import styled from 'styled-components';
import { ContextMenu, TooltipBody, UnbookReasonSelect, FlexColumn } from '@ubeya/shared-web/components';
import ConfigContext from '@ubeya/shared-web/contexts/ConfigContext';
import PrivilegesContext from '@ubeya/shared-web/contexts/PrivilegesContext';
import privileges from '@ubeya/shared-web/privileges';
import usePrivileges from '@ubeya/shared/hooks/generic/usePrivileges';
import useBranches from '@ubeya/shared/hooks/account/useBranches';
import { usePermissionGate } from '@ubeya/shared/contexts/PermissionGateContext';
import { PAYROLL_PROCESS_STATUS } from '@ubeya/shared-web/constants';
import { ACCOUNT_BILLING_STATUS_MAP } from '@ubeya/shared/constants';
import useToaster from '@ubeya/shared/hooks/useToaster';
import { trackEvent } from '@ubeya/shared-web/analytics/amplitude';
import TimesheetAutoFillModal from '../TimesheetAutoFillModal';

const UnbookPromptWrapper = styled(FlexColumn)`
  gap: 12px;
`;

// ContextMenu snapshots this element when the menu item is clicked and never re-reads it, so a value
// held by the parent could not get back in - the picker keeps its own state for display and reports
// through a ref that the (equally snapshotted) confirm handler reads at submit time. Mounted only
// while the prompt is open, so clearing on mount drops a pick left behind by a cancelled prompt.
const UnbookPromptBody = ({ unbookReasonIdRef, message, title, allowNoShowReasons }) => {
  const [unbookReasonId, setUnbookReasonId] = useState(null);

  useEffect(() => {
    unbookReasonIdRef.current = null;
    return () => {
      unbookReasonIdRef.current = null;
    };
  }, [unbookReasonIdRef]);

  const handleChange = useCallback(
    (value) => {
      unbookReasonIdRef.current = value;
      setUnbookReasonId(value);
    },
    [unbookReasonIdRef]
  );

  return (
    <UnbookPromptWrapper>
      <div>{message}</div>
      <UnbookReasonSelect
        value={unbookReasonId}
        onChange={handleChange}
        title={title}
        allowNoShowReasons={allowNoShowReasons}
        optional
      />
    </UnbookPromptWrapper>
  );
};

const ActionsRenderer = ({
  rowData,
  rowData: { summarized, isJob, isPto, isLeave, actualIn, actualOut },
  columnData: {
    onClickShift,
    onPayQueryClick,
    onRevertPayrollProcessClick,
    createDailyShift,
    deleteTimeSheet,
    crossAccountUnbookEmployee,
    autofill,
    hasActionPermission,
    showPayQueryFeature = false,
    // Opt-in: only tables whose onPayQueryClick routes absences to the removal endpoints set this.
    allowAbsenceRemoval = false,
    readOnly = false,
    amplitudeModuleName
  }
}) => {
  const { t, enablePayrollPeriodRequeryFeature, billingStatus } = useContext(ConfigContext);
  const myPrivileges = useContext(PrivilegesContext);
  const { mappedPrivileges } = usePrivileges();
  const { mappedBranches } = useBranches();
  const { contextMenuPermissionGate } = usePermissionGate();
  const { addError, addResponseError } = useToaster();
  const [autoFillHoursModal, setAutoFillHoursModal] = useState(false);
  const unbookReasonIdRef = useRef(null);
  const allowToAddDailyJobShift = useMemo(() => !!actualIn && !!actualOut && !!isJob, [actualIn, actualOut, isJob]);
  // Mirrors the backend's Premium check (PremiumClientAccountValidatorUseCase): paying or internal only.
  const isPremiumClient = [ACCOUNT_BILLING_STATUS_MAP.PAYING, ACCOUNT_BILLING_STATUS_MAP.INTERNAL].includes(
    billingStatus
  );
  const hasPrivilegeToUnbookEmployee =
    !!myPrivileges[privileges.APPROVE_TIMESHEET] || !!myPrivileges[privileges.EDIT_SCHEDULING];

  // A PTO or leave row is an absence: no shift to edit, unbook or fill hours, and no payroll snapshot to
  // unlock for a pay query — the equivalent action is removing it, which leaves the next payroll run a
  // reversal to send. Only offered where the account's payroll runs know how to reverse already-reported
  // pay. It is the only action an absence row has at all, so an absence that can't be removed renders no
  // menu rather than an empty one.
  const isAbsence = !!isPto || !!isLeave;

  // The removal endpoint gates on both privileges, so the menu item has to as well or it 403s.
  const canRemoveProcessedAbsence =
    isAbsence &&
    rowData.payrollProcess?.status === PAYROLL_PROCESS_STATUS.PROCESSED &&
    !!enablePayrollPeriodRequeryFeature &&
    showPayQueryFeature &&
    allowAbsenceRemoval &&
    !!myPrivileges[privileges.MANAGE_EMPLOYEE_HOLIDAY_ACCRUAL];

  const canRevertPayrollProcess =
    !!onRevertPayrollProcessClick && !!rowData?.payrollProcess && !rowData?.dummy && !rowData?.isExternal;

  const handleAutoFillClick = useCallback(({ override = true }) => autofill({ rows: [rowData], override }), [
    rowData,
    autofill
  ]);

  // todo: update TimesheetTableGrid after feature merge

  const handleUnbookFromPayroll = useCallback(async () => {
    const unbookReasonId = unbookReasonIdRef.current;
    await deleteTimeSheet({ ...rowData, isSoftDelete: false, unbookReasonId });
    unbookReasonIdRef.current = null;
    const employeeId = rowData?.employee?.id;
    if (employeeId == null || !amplitudeModuleName) {
      return;
    }
    const analyticsModule = amplitudeModuleName.startsWith('Payroll') ? 'Payroll' : 'Timesheet';
    trackEvent('Employee Unbooked', {
      module: analyticsModule,
      context: amplitudeModuleName,
      projectId: rowData.projectId,
      shiftId: rowData.shiftId,
      employeeId,
      origin: amplitudeModuleName
    });
  }, [amplitudeModuleName, deleteTimeSheet, rowData]);

  const handleCrossAccountUnbook = useCallback(async () => {
    const unbookReasonId = unbookReasonIdRef.current;

    try {
      await crossAccountUnbookEmployee({
        branchId: rowData.branchId,
        projectId: rowData.projectId,
        slotId: rowData.id,
        // The server resolves No Show status from this id itself; it is never sent as a boolean from here.
        unbookReasonId
      });
    } catch (error) {
      const errorCode = error?.response?.data?.errorCode;
      if (errorCode === 'premiumClientOnlyFeature') {
        addError(t('unbookExternalEmployeePremiumOnlyError'));
        return;
      }
      // Other errors (e.g. notACrossAccountUnbook) fall through to the same generic handling every
      // other API error on this page gets.
      addResponseError(error);
      return;
    }

    unbookReasonIdRef.current = null;
    const employeeId = rowData?.employee?.id;
    if (employeeId == null || !amplitudeModuleName) {
      return;
    }
    const analyticsModule = amplitudeModuleName.startsWith('Payroll') ? 'Payroll' : 'Timesheet';
    trackEvent('Employee Unbooked', {
      module: analyticsModule,
      context: amplitudeModuleName,
      projectId: rowData.projectId,
      shiftId: rowData.shiftId,
      employeeId,
      origin: amplitudeModuleName,
      isExternal: true,
      supplierAccountId: rowData.supplierAccountId,
      unbookReasonId
    });
  }, [addError, addResponseError, amplitudeModuleName, crossAccountUnbookEmployee, rowData, t]);

  const defineTooltip = useCallback(
    (isLocked, hasBeenProcessed) => {
      if (!hasPrivilegeToUnbookEmployee) {
        return (
          <TooltipBody
            center
            maxWidth={250}
            body={t('missingPrivilege', {
              privilege:
                mappedPrivileges[privileges.EDIT_SCHEDULING] + ' / ' + mappedPrivileges[privileges.APPROVE_TIMESHEET]
            })}
          />
        );
      }
      if (isLocked) {
        return <TooltipBody center maxWidth={300} body={t('tooltipCannotUnbookAsHoursAreAlreadyApproved')} />;
      }
      if (hasBeenProcessed) {
        return <TooltipBody center maxWidth={300} body={t('disabledUnbookWhenProcessed')} />;
      }

      return undefined;
    },
    [hasPrivilegeToUnbookEmployee, mappedPrivileges, t]
  );

  const shiftOptions = useMemo(() => {
    const notDummyOrExternal = !rowData.dummy && !rowData.isExternal;
    // A dummy (planned job without a timesheet) is filled from the branch default job hours
    const { defaultJobStartTime, defaultJobEndTime } = mappedBranches[rowData.branchId]?.timesheetConfig || {};
    const canAutoFillDummy = !!rowData.dummy && !!defaultJobStartTime && !!defaultJobEndTime;
    const hasClockInOut = rowData.actualIn && rowData.actualOut;
    const showOnlyToUnlockPayrollAdmins = !rowData.approved || hasPrivilegeToUnbookEmployee;
    const rowProcessStatus = rowData.payrollProcess?.status;
    const rowIsProcessed = rowProcessStatus === PAYROLL_PROCESS_STATUS.PROCESSED || false;
    const isShiftEditable = !rowIsProcessed && !rowData?.isLocked;
    const canPayQuery = notDummyOrExternal && rowIsProcessed && !isAbsence;
    const canUnbookEmployees = hasPrivilegeToUnbookEmployee && !rowData.isLocked && !rowProcessStatus;
    // Premium clients may unbook an external employee too — every other action stays excluded for
    // external rows (still gated by notDummyOrExternal below).
    // Only where the page wires the cross-account unbook (the payroll tables share this renderer).
    // Same as the booking board: a supplier shift not mapped to a client shift (floating) is the
    // supplier's to manage, so no clientShiftId means no unbook.
    const canUnbookExternalEmployee =
      !!crossAccountUnbookEmployee &&
      !!rowData.isExternal &&
      !rowData.dummy &&
      !!rowData.clientShiftId &&
      isPremiumClient;
    // Same rule as the booking board: once either side approved the hours, the external booking stays.
    const externalHoursApproved = !!rowData.isExternal && (!!rowData.approved || !!rowData.clientApproved);
    // An external row's scheduleIn is the client shift's start; the client can unbook only once it started.
    const externalShiftNotStarted =
      !!rowData.isExternal && (!rowData.scheduleIn || moment().isBefore(moment(rowData.scheduleIn)));

    const options = [
      {
        label: t('shiftDetails'),
        onClick: () => onClickShift(rowData),
        actionRequiredPrivilege: [privileges.EDIT_SCHEDULING],
        readRequiredPrivilege: [privileges.EDIT_SCHEDULING],
        customTooltip:
          !myPrivileges[privileges.EDIT_SCHEDULING] &&
          t('missingPrivilege', { privilege: mappedPrivileges[privileges.EDIT_SCHEDULING] }),
        visible: !isAbsence
      },
      {
        label: t('addAnotherShift'),
        onClick: () => createDailyShift({ ...rowData, hasAddedAnotherShift: true }),
        visible: allowToAddDailyJobShift
      },
      {
        label: t('autoFillHours'),
        onClick: () => setAutoFillHoursModal(true),
        customTooltip: !isShiftEditable ? t('cantUpdateLockedShiftsTooltip') : null,
        disabled: !isShiftEditable || !hasActionPermission,
        visible: (notDummyOrExternal || canAutoFillDummy) && !isAbsence
      },
      {
        label: t('deleteShiftHours'),
        onClick: () => deleteTimeSheet({ ...rowData }),
        confirm: true,
        confirmBody: t('areYouSureDeleteShift'),
        confirmText: 'delete',
        confirmPortal: true,
        confirmIsWaitingForResponse: false,
        customTooltip: !isShiftEditable ? t('cantUpdateLockedShiftsTooltip') : null,
        disabled: !isShiftEditable || !hasActionPermission,
        visible: notDummyOrExternal && hasClockInOut && showOnlyToUnlockPayrollAdmins
      },
      {
        label: canRemoveProcessedAbsence ? t('removeForPayrollQuery') : t('unlockForPayrollQuery'),
        onClick: () => onPayQueryClick(rowData),
        // Removing an absence is destructive in a way unlocking a slot is not — it deletes the record.
        confirm: canRemoveProcessedAbsence,
        confirmBody: t('areYouSureRemoveAbsenceForPayrollQuery'),
        confirmText: 'remove',
        confirmPortal: true,
        confirmIsWaitingForResponse: false,
        visible: showPayQueryFeature && (canPayQuery || canRemoveProcessedAbsence)
      },
      {
        label: t('unbook'),
        onClick: rowData.isExternal ? handleCrossAccountUnbook : handleUnbookFromPayroll,
        confirm: true,
        confirmBody: t('areYouSureUnbookEmployee'),
        // Job and PTO rows unbook through deleteJob, which takes no reason - so the picker is
        // not offered there rather than silently discarding the choice.
        // An external employee gets the same reason picker; no admin notes or message - the unbooking
        // belongs to the employee's own account, and the employee gets the standard notification.
        confirmRenderBody:
          !rowData.isExternal && (rowData?.isJob || rowData?.isPto) ? undefined : (
            <UnbookPromptBody
              unbookReasonIdRef={unbookReasonIdRef}
              message={t('areYouSureUnbookEmployee')}
              title={t('unbookReason')}
              // Same rule as the booking board's external unbook: no-show reasons only from 24 hours
              // before the shift starts (the server rejects unconfirmed external unbooks on its own).
              allowNoShowReasons={
                !rowData.isExternal || moment().isSameOrAfter(moment(rowData.scheduleIn).subtract(24, 'hours'))
              }
            />
          ),
        confirmText: 'unbook',
        confirmPortal: true,
        confirmIsWaitingForResponse: false,
        customTooltip: externalHoursApproved ? (
          <TooltipBody center maxWidth={300} body={t('tooltipCannotUnbookAsHoursAreAlreadyApproved')} />
        ) : externalShiftNotStarted ? (
          <TooltipBody center maxWidth={300} body={t('cannotUnbookExternalEmployeeBeforeShiftStarts')} />
        ) : (
          !canUnbookEmployees && defineTooltip(!!rowData?.isLocked, !!rowProcessStatus)
        ),
        disabled: !canUnbookEmployees || externalHoursApproved || externalShiftNotStarted,
        visible: (notDummyOrExternal || canUnbookExternalEmployee) && !isAbsence
      },
      {
        label: t('revertPayrollProcess'),
        onClick: () => onRevertPayrollProcessClick?.(rowData),
        confirm: true,
        confirmTitle: t('revertPayrollProcessItemConfirmTitle'),
        confirmBody: t('revertPayrollProcessItemConfirmBody'),
        confirmText: 'revertPayrollProcessHistory',
        isPermanentAction: true,
        confirmPortal: true,
        confirmIsWaitingForResponse: false,
        visible: canRevertPayrollProcess
      }
    ].map((item) => contextMenuPermissionGate(item));

    return options;
  }, [
    rowData,
    hasPrivilegeToUnbookEmployee,
    isPremiumClient,
    crossAccountUnbookEmployee,
    t,
    hasActionPermission,
    myPrivileges,
    mappedPrivileges,
    mappedBranches,
    allowToAddDailyJobShift,
    showPayQueryFeature,
    isAbsence,
    canRemoveProcessedAbsence,
    canRevertPayrollProcess,
    defineTooltip,
    onClickShift,
    createDailyShift,
    deleteTimeSheet,
    handleUnbookFromPayroll,
    handleCrossAccountUnbook,
    onPayQueryClick,
    onRevertPayrollProcessClick,
    contextMenuPermissionGate
  ]);

  const onCloseAutoFillHoursModal = useCallback(() => setAutoFillHoursModal(false), []);

  // Removing it is an absence row's only action, so without it there is nothing to open — render no menu
  // at all rather than an empty one.
  if (summarized || readOnly || (isAbsence && !canRemoveProcessedAbsence && !canRevertPayrollProcess)) {
    return <></>;
  }

  return (
    <>
      <ContextMenu options={shiftOptions} usePortal />

      {autoFillHoursModal && (
        <TimesheetAutoFillModal autofill={handleAutoFillClick} onClose={onCloseAutoFillHoursModal} />
      )}
    </>
  );
};

export default ActionsRenderer;
